import { WorkCalendar } from "@/engine/calendar";
import { computeSchedule } from "@/engine/cpm";
import { diffDays } from "@/engine/date";
import type { Project, Schedule } from "@/engine/types";
import { peopleOnPlan } from "@/features/access/own-assignment";

export type PlanTone = "empty" | "active" | "done" | "late";

export type PlanSnapshot = {
  finish: string;
  pct: number;
  tone: PlanTone;
  people: string[];
  taskCount: number;
  lifecycle: "active" | "done" | "archived" | "trashed";
};

export type PlanScope = "all" | "active" | "done" | "late";
export type PlanSort = "updated" | "name" | "finish";
export type PlanView = "grid" | "list";

export const PLAN_SCOPES: { value: PlanScope; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "active", label: "진행 중" },
  { value: "done", label: "완료" },
  { value: "late", label: "지연" },
];

export const PLAN_SORTS: { value: PlanSort; label: string }[] = [
  { value: "updated", label: "편집" },
  { value: "name", label: "이름" },
  { value: "finish", label: "종료일" },
];

/** 목록이 기본이다. `?view=grid`만 카드다. */
export function parsePlanView(raw: string | null): PlanView {
  return raw === "grid" ? "grid" : "list";
}

/** 제목 아래 한 줄. 없는 상태는 빼서, 범위를 바꿔도 같은 문장이다. */
export function planListScale(rows: readonly { tone: PlanTone; lifecycle: PlanSnapshot["lifecycle"] }[]): string {
  let active = 0;
  let done = 0;
  let late = 0;
  let empty = 0;
  let archived = 0;
  for (const row of rows) {
    if (row.lifecycle === "archived") {
      archived += 1;
      continue;
    }
    if (row.lifecycle === "done" || row.tone === "done") done += 1;
    else if (row.tone === "late") late += 1;
    else if (row.tone === "empty") empty += 1;
    else active += 1;
  }
  const parts = [`${rows.length}개`];
  if (active) parts.push(`진행 ${active}`);
  if (done) parts.push(`완료 ${done}`);
  if (late) parts.push(`지연 ${late}`);
  if (empty) parts.push(`일정 없음 ${empty}`);
  if (archived) parts.push(`아카이브 ${archived}`);
  return parts.join(" · ");
}

/** 이름에 검색어가 있으면 남긴다. 빈 검색은 전체를 통과시킨다. */
export function planMatchesQuery(name: string, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase("ko");
  if (!needle) return true;
  return name.toLocaleLowerCase("ko").includes(needle);
}

export type PlanGalleryRow = {
  id: string;
  name: string;
  updatedAt: string;
  finish: string;
  tone: PlanTone;
};

function overallPct(schedule: Schedule): number {
  const leaves = schedule.order.filter((id) => !schedule.tasks.get(id)?.isSummary);
  if (!leaves.length) return 0;
  const sum = leaves.reduce((total, id) => total + (schedule.tasks.get(id)?.percentComplete ?? 0), 0);
  return Math.round(sum / leaves.length);
}

function toneOf(taskCount: number, pct: number, finish: string, today: string, override?: Project["statusOverride"]): PlanTone {
  if (override === "done") return "done";
  if (override === "active") return "active";
  if (taskCount === 0) return "empty";
  if (pct >= 100) return "done";
  if (finish && finish < today) return "late";
  return "active";
}

/** 완료·지연일 때만 카드에 적는 짧은 상태. 진행 중은 표지가 말한다. */
export function planStatusLabel(tone: PlanTone, finish: string, today: string): string | null {
  if (tone === "done") return "완료";
  if (tone !== "late" || !finish) return null;
  const late = diffDays(finish, today);
  return late > 0 ? `D+${late}` : "지연";
}

/** 설정에 올리는 마감과 Task 수. 완료·일정 없음에는 마감을 적지 않는다. */
export function planScheduleNote(
  snap: Pick<PlanSnapshot, "tone" | "finish" | "taskCount">,
  today: string,
): { time: string; tasks: string } {
  const tasks = snap.taskCount > 0 ? `Task ${snap.taskCount}개` : "Task 없음";
  let time = "";
  if (snap.finish && snap.tone !== "empty" && snap.tone !== "done") {
    if (snap.finish === today) time = "오늘 마감";
    else if (snap.finish > today) time = `${diffDays(today, snap.finish)}일 남음`;
    else {
      const late = diffDays(snap.finish, today);
      if (late > 0) time = `${late}일 지남`;
    }
  }
  return { time, tasks };
}

/** 카드 표지에 올리는 상태와 한 줄. 아카이브가 일정 상태보다 앞선다. */
export function planCover(snap: Pick<PlanSnapshot, "tone" | "lifecycle" | "finish" | "taskCount">, today: string): { status: string; detail: string } {
  const status =
    snap.lifecycle === "archived"
      ? "아카이브"
      : snap.lifecycle === "done" || snap.tone === "done"
        ? "완료"
        : snap.tone === "late"
          ? "지연"
          : snap.tone === "empty"
            ? "일정 없음"
            : "진행 중";
  const note = planScheduleNote(snap, today);
  return { status, detail: [note.time, note.tasks].filter(Boolean).join(" · ") };
}

/** 배정된 팀. 팀이 없으면 일정·작업의 담당. */
export function planCollaborators(project: Project, resourceNames: Readonly<Record<string, string>>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (name?: string) => {
    const next = name?.trim();
    if (!next || seen.has(next)) return;
    seen.add(next);
    out.push(next);
  };
  for (const assignment of project.resourceAssignments) push(resourceNames[assignment.resourceId]);
  if (out.length > 0) return out;
  for (const person of peopleOnPlan(project.tasks, resourceNames)) push(person.name);
  for (const task of project.executionTasks) {
    for (const name of task.assignees ?? []) push(name);
  }
  return out;
}

export function inspectPlan(project: Project, today: string, resourceNames: Readonly<Record<string, string>> = {}): PlanSnapshot {
  const people = planCollaborators(project, resourceNames);
  try {
    const schedule = computeSchedule(project, new WorkCalendar(project.calendar));
    const finish = schedule.projectFinish || project.start;
    const pct = overallPct(schedule);
    return {
      finish,
      pct,
      tone: project.lifecycle === "done" ? "done" : toneOf(project.tasks.length, pct, finish, today, project.statusOverride),
      people,
      taskCount: project.executionTasks.length,
      lifecycle: project.lifecycle ?? "active",
    };
  } catch {
    return {
      finish: project.start,
      pct: 0,
      tone: project.lifecycle === "done" ? "done" : toneOf(project.tasks.length, 0, project.start, today, project.statusOverride),
      people,
      taskCount: project.executionTasks.length,
      lifecycle: project.lifecycle ?? "active",
    };
  }
}

function inScope(tone: PlanTone, scope: PlanScope) {
  if (scope === "all") return true;
  if (scope === "active") return tone === "active" || tone === "empty";
  return tone === scope;
}

function comparePlan(a: PlanGalleryRow, b: PlanGalleryRow, sort: PlanSort) {
  if (sort === "name") return a.name.localeCompare(b.name, "ko") || a.id.localeCompare(b.id);
  if (sort === "finish") {
    if (!a.finish && !b.finish) return a.id.localeCompare(b.id);
    if (!a.finish) return 1;
    if (!b.finish) return -1;
    if (a.finish !== b.finish) return a.finish < b.finish ? -1 : 1;
    return a.id.localeCompare(b.id);
  }
  if (a.updatedAt !== b.updatedAt) return a.updatedAt < b.updatedAt ? 1 : -1;
  return a.id.localeCompare(b.id);
}

/** 범위로 좁힌 뒤 정렬한다. 입력 배열은 바꾸지 않는다. */
export function arrangePlans<T extends PlanGalleryRow>(rows: readonly T[], scope: PlanScope, sort: PlanSort): T[] {
  return rows.filter((row) => inScope(row.tone, scope)).sort((a, b) => comparePlan(a, b, sort));
}
